import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  DestroyRef,
  inject,
  ChangeDetectorRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatFormFieldModule } from '@angular/material/form-field';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { filter } from 'rxjs/operators';

import { TsButtonModule } from 'tsui';
import { Todo, Task } from '@tasks/tasks.types';
import { ConfirmService } from 'app/shared/components/widgets/dialog/confirm/confirm.service';
import { DirectivesModule } from '@shared/directives/directives.module';
import { MatDividerModule } from '@angular/material/divider';

@Component({
  selector: 'task-details-checklist',
  templateUrl: './task-details-checklist.component.html',
  styleUrls: ['./task-details-checklist.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatTooltipModule,
    DragDropModule,
    TsButtonModule,
    DirectivesModule,
    MatDividerModule,
  ],
})
export class TaskDetailsChecklistComponent {
  private originalTodos: { [id: number]: Todo } = {};

  private readonly destroyRef = inject(DestroyRef);
  private readonly confirmService = inject(ConfirmService);
  private readonly cdRef = inject(ChangeDetectorRef);

  @Input({ required: true }) task!: Task;
  @Input() canEdit = true;
  @Output() checkListUpdated = new EventEmitter<Task>();

  private resetTodoOrder(todos: Todo[]): void {
    let index = 0;
    for (let todo of todos) {
      todo.rank = index;
      index += 1;
    }
  }

  createTodo(): void {
    const todo: Todo = {
      title: '',
      done: false,
      editing: true,
    };

    this.task.todos = this.task.todos || [];
    this.task.todos.push(todo);
  }

  deleteTodo(todo: Todo): void {
    this.confirmService
      .openConfirmationModal()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((result: boolean) => result),
      )
      .subscribe(() => {
        todo['_destroy'] = true;
        this.checkListUpdated.emit(this.task);
      });
  }

  saveTodo(todo: Todo): void {
    todo.editing = false;
    if (todo.id) {
      delete this.originalTodos[todo.id];
    }
    this.checkListUpdated.emit(this.task);
  }

  editTodo(todo: Todo): void {
    if (todo.id && !this.originalTodos[todo.id]) {
      this.originalTodos[todo.id] = { ...todo };
    }
    todo.editing = true;
  }

  cancelTodoEditing(todo: Todo): void {
    if (!todo.id) {
      // This is a new todo that hasn't been saved to the backend.
      if (todo.title.trim().length > 0) {
        // User has entered some text, prompt for confirmation.
        this.confirmService
          .openConfirmationModal({
            title: 'Discard new item?',
            message: 'Are you sure you want to discard this new checklist item?',
          })
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((result: boolean) => {
            if (result) {
              this.task.todos = this.task.todos.filter((t) => t !== todo);
            }
            this.cdRef.detectChanges();
          });
      } else {
        // Empty new todo, remove it without confirmation.
        this.task.todos = this.task.todos.filter((t) => t !== todo);
      }
    } else {
      // Existing todo, revert changes if any were made.
      const originalTodo = this.originalTodos[todo.id];

      if (originalTodo && (originalTodo.title !== todo.title || todo.editing)) {
        this.confirmService
          .openConfirmationModal({
            title: 'Discard changes?',
            message: 'Are you sure you want to discard the changes?',
          })
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((result: boolean) => {
            if (result) {
              Object.assign(todo, originalTodo);
              todo.editing = false;
              delete this.originalTodos[todo.id];
            }
            this.cdRef.detectChanges();
          });
      } else {
        todo.editing = false;
        if (originalTodo) {
          delete this.originalTodos[todo.id];
        }
        this.cdRef.detectChanges();
      }
    }
  }

  toggleTodoCompleted(todo: Todo): void {
    todo.done = !todo.done;
    this.checkListUpdated.emit(this.task);
  }

  dropped(event: CdkDragDrop<Todo[]>): void {
    moveItemInArray(this.task.todos, event.previousIndex, event.currentIndex);
    this.resetTodoOrder(this.task.todos);
    this.checkListUpdated.emit(this.task);
  }
}
